import { animate, timeline } from 'vendor';
import { EffectCarousel } from 'theme';

const easing = [0.645, 0.045, 0.355, 1];

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Finish Prestige's own controls so reduced motion preserves its final styles
// and disclosure cleanup, including when the preference changes between toggles.
await customElements.whenDefined('accordion-disclosure');
const AccordionDisclosure = customElements.get('accordion-disclosure');
const nativeAccordionShow = AccordionDisclosure.prototype.createShowAnimationControls;
const nativeAccordionHide = AccordionDisclosure.prototype.createHideAnimationControls;

AccordionDisclosure.prototype.createShowAnimationControls = function() {
  const controls = nativeAccordionShow.call(this);
  if (prefersReducedMotion()) controls.finish();
  return controls;
};

AccordionDisclosure.prototype.createHideAnimationControls = function() {
  const controls = nativeAccordionHide.call(this);
  if (prefersReducedMotion()) controls.finish();
  return controls;
};

function createOverlayAnimationControls(element, entering, hiddenTransform, duration) {
  return timeline([
    [element.getShadowPartByName('overlay'), { opacity: entering ? [0, 1] : [1, 0] }, { duration, easing }],
    [element.getShadowPartByName('content'), { transform: entering ? [hiddenTransform, 'translateX(0)'] : ['translateX(0)', hiddenTransform] }, { duration, at: '<', easing }]
  ]);
}

function createBottomSheetAnimationControls(element, entering, duration) {
  return timeline([
    [element.getShadowPartByName('overlay'), { opacity: entering ? [0, 1] : [1, 0] }, { duration, easing }],
    [element.getShadowPartByName('content'), { transform: entering ? ['translateY(100%)', 'translateY(0)'] : ['translateY(0)', 'translateY(100%)'] }, { duration, at: '<', easing }]
  ]);
}

function createDrawerAnimationControls(element, entering, duration) {
  const hiddenTransform = `translateX(calc(var(--transform-logical-flip) * ${element.openFrom === 'right' ? '100%' : '-100%'}))`;
  if (entering) element.getShadowPartByName('content').style.marginInlineStart = element.openFrom === 'right' ? 'auto' : 0;
  return createOverlayAnimationControls(element, entering, hiddenTransform, duration);
}

// Preserve JOMO's desktop side-panel treatment while keeping Prestige 11's
// modal lifecycle, focus management and Shadow DOM templates.
await customElements.whenDefined('x-modal');
const Modal = customElements.get('x-modal');

Modal.prototype.createEnterAnimationControls = function() {
  const duration = prefersReducedMotion() ? 0 : 0.3;

  if (window.matchMedia('(min-width: 700px)').matches) {
    return createOverlayAnimationControls(this, true, 'translateX(calc(var(--transform-logical-flip) * 100%))', duration);
  }

  return createBottomSheetAnimationControls(this, true, duration);
};

Modal.prototype.createLeaveAnimationControls = function() {
  const duration = prefersReducedMotion() ? 0 : 0.3;

  if (window.matchMedia('(min-width: 700px)').matches) {
    return createOverlayAnimationControls(this, false, 'translateX(calc(var(--transform-logical-flip) * 100%))', duration);
  }

  return createBottomSheetAnimationControls(this, false, duration);
};

// Facets remain a side drawer on desktop and become a bottom sheet below the
// Prestige md breakpoint, matching the customized storefront interaction.
await customElements.whenDefined('facets-drawer');
const FacetsDrawer = customElements.get('facets-drawer');
const DrawerPrototype = Object.getPrototypeOf(FacetsDrawer.prototype);
const nativeFacetsEnter = DrawerPrototype.createEnterAnimationControls;
const nativeFacetsLeave = DrawerPrototype.createLeaveAnimationControls;

FacetsDrawer.prototype.createEnterAnimationControls = function() {
  const reducedMotion = prefersReducedMotion();

  if (window.matchMedia('(max-width: 999px)').matches) {
    return createBottomSheetAnimationControls(this, true, reducedMotion ? 0 : 0.3);
  }

  if (reducedMotion) return createDrawerAnimationControls(this, true, 0);
  return nativeFacetsEnter.call(this);
};

FacetsDrawer.prototype.createLeaveAnimationControls = function() {
  const reducedMotion = prefersReducedMotion();

  if (window.matchMedia('(max-width: 999px)').matches) {
    return createBottomSheetAnimationControls(this, false, reducedMotion ? 0 : 0.3);
  }

  if (reducedMotion) return createDrawerAnimationControls(this, false, 0);
  return nativeFacetsLeave.call(this);
};

document.addEventListener('dialog:before-show', (event) => {
  if (event.target?.localName === 'header-search') {
    event.target.classList.add('is-active');
  }

  if (event.target?.localName !== 'quick-buy-modal') return;

  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer?.open) cartDrawer.hide();
}, true);

// Keep search above the sticky purchase bar until its closing animation finishes.
document.addEventListener('dialog:after-hide', (event) => {
  if (event.target?.localName === 'header-search' && !event.target.open) {
    event.target.classList.remove('is-active');
  }

  if (event.target?.localName === 'header-sidebar' && !event.target.open) {
    // Prestige closes these details in its target listener. Reset the state
    // used by the accordion toggle as well, so the next click opens it once.
    event.target.querySelectorAll('accordion-disclosure > details').forEach((details) => {
      details.setAttribute('aria-expanded', 'false');
    });
  }
}, true);

function enforceRecentlyViewedThreshold() {
  let recentlyViewedIds = [];
  try {
    recentlyViewedIds = JSON.parse(localStorage.getItem('theme:recently-viewed-products') || '[]');
  } catch (_) {
    // A malformed visitor-local value should behave like an empty history.
  }

  if (!Array.isArray(recentlyViewedIds) || recentlyViewedIds.length <= 4) {
    document.querySelectorAll('recently-viewed-products[data-is-home-page]').forEach((element) => element.closest('.shopify-section')?.remove());
  }
}

class VariantTitle extends HTMLElement {
  connectedCallback() {
    this.form = document.forms[this.getAttribute('form')];
    this.onVariantChanged = this.onVariantChanged.bind(this);
    this.form?.addEventListener('variant:change', this.onVariantChanged);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('variant:change', this.onVariantChanged);
  }

  onVariantChanged(event) {
    if (event.detail.variant) this.textContent = event.detail.variant.title;
  }
}

if (!customElements.get('variant-title')) customElements.define('variant-title', VariantTitle);

class TextSlideshowCarousel extends EffectCarousel {
  createOnChangeAnimationControls(fromSlide, toSlide) {
    if (prefersReducedMotion()) {
      return {
        leaveControls: () => animate(fromSlide, { opacity: [1, 0], transform: ['translateY(0)', 'translateY(-10px)'] }, { duration: 0 }),
        enterControls: () => animate(toSlide, { opacity: [0, 1], transform: ['translateY(10px)', 'translateY(0)'] }, { duration: 0 })
      };
    }

    return {
      leaveControls: () => animate(fromSlide, { opacity: [1, 0], transform: ['translateY(0)', 'translateY(-10px)'] }, { duration: 0.25, easing: [0.55, 0.055, 0.675, 0.19] }),
      enterControls: () => animate(toSlide, { opacity: [0, 1], transform: ['translateY(10px)', 'translateY(0)'] }, { duration: 0.4, easing: [0.215, 0.61, 0.355, 1] })
    };
  }
}

if (!customElements.get('text-slideshow-carousel')) customElements.define('text-slideshow-carousel', TextSlideshowCarousel);

enforceRecentlyViewedThreshold();
document.addEventListener('shopify:section:load', () => {
  enforceRecentlyViewedThreshold();
});
